import React, { useEffect, useState } from "react";
import { supabase } from "../supabase/client";
import {
  FiCalendar,
  FiCheckCircle,
  FiXCircle,
  FiRefreshCw,
  FiFilter,
  FiUser,
} from "react-icons/fi";
import "./Checkins.css";

const Checkins = () => {
  const [checkins, setCheckins] = useState([]);
  const [date, setDate] = useState("");
  const [validatedOnly, setValidatedOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchCheckins = async () => {
    setLoading(true);

    try {
      // Fetch check-ins with explicit relationship specification
      const { data: checkinsData, error: checkinsError } = await supabase
        .from("check_ins")
        .select(`
          id, 
          check_in_time, 
          validated, 
          user_id, 
          carwash_id, 
          username,
          users!check_ins_user_id_fkey (email),  
          carwashes (name)
        `)
        .order("check_in_time", { ascending: false });

      if (checkinsError) {
        console.error("Error fetching check-ins:", checkinsError);

        // If the explicit relationship fails, try fetching data separately
        await fetchCheckinsSeparately();
        return;
      }

      let filtered = checkinsData || [];

      if (date) {
        const selectedDate = new Date(date);
        const start = new Date(selectedDate.setHours(0, 0, 0, 0)).toISOString();
        const end = new Date(
          selectedDate.setHours(23, 59, 59, 999)
        ).toISOString();
        filtered = filtered.filter(
          (c) => c.check_in_time >= start && c.check_in_time <= end
        );
      }

      if (validatedOnly) {
        filtered = filtered.filter((c) => c.validated);
      }

      setCheckins(filtered);
    } catch (error) {
      console.error("Error in fetchCheckins:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fallback method if the join fails
  const fetchCheckinsSeparately = async () => {
    try {
      // Fetch check-ins without joins
      const { data: checkinsData, error: checkinsError } = await supabase
        .from("check_ins")
        .select("id, check_in_time, validated, user_id, carwash_id, username")
        .order("check_in_time", { ascending: false });

      if (checkinsError) throw checkinsError;

      // Fetch users separately
      const { data: usersData, error: usersError } = await supabase
        .from("users")
        .select("id, email");

      if (usersError) throw usersError;

      // Fetch carwashes separately
      const { data: carwashesData, error: carwashesError } = await supabase
        .from("carwashes")
        .select("id, name");

      if (carwashesError) throw carwashesError;

      // Combine the data
      const combinedData = checkinsData.map(checkin => {
        const user = usersData?.find(u => u.id === checkin.user_id);
        const carwash = carwashesData?.find(c => c.id === checkin.carwash_id);

        return {
          ...checkin,
          users: user ? { email: user.email } : null,
          carwashes: carwash ? { name: carwash.name } : null
        };
      });

      let filtered = combinedData || [];

      if (date) {
        const selectedDate = new Date(date);
        const start = new Date(selectedDate.setHours(0, 0, 0, 0)).toISOString();
        const end = new Date(
          selectedDate.setHours(23, 59, 59, 999)
        ).toISOString();
        filtered = filtered.filter(
          (c) => c.check_in_time >= start && c.check_in_time <= end
        );
      }

      if (validatedOnly) {
        filtered = filtered.filter((c) => c.validated);
      }

      setCheckins(filtered);
    } catch (error) {
      console.error("Error in fetchCheckinsSeparately:", error);
    }
  };

  const resetCheckin = async (id) => {
    const { error } = await supabase
      .from("check_ins")
      .update({ validated: false })
      .eq("id", id);

    if (!error) {
      fetchCheckins();
    } else {
      console.error("Error resetting check-in:", error);
    }
  };

  const validateCheckin = async (id) => {
    const { error } = await supabase
      .from("check_ins")
      .update({ validated: true })
      .eq("id", id);

    if (!error) {
      fetchCheckins();
    } else {
      console.error("Error validating check-in:", error);
    }
  };

  const deleteCheckin = async (id) => {
    if (!window.confirm("Are you sure you want to delete this check-in? This action cannot be undone.")) {
      return;
    }

    const { error } = await supabase
      .from("check_ins")
      .delete()
      .eq("id", id);

    if (!error) {
      fetchCheckins();
    } else {
      console.error("Error deleting check-in:", error);
    }
  };

  useEffect(() => {
    fetchCheckins();
  }, [date, validatedOnly]);

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const checkinTime = new Date(dateString);
    const diffMs = now - checkinTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="checkins-container">
      <div className="page-header">
        <h2 className="page-title">
          <FiCalendar /> Check-In Log
        </h2>

        <div className="filter-controls">
          <div className="filter-group">
            <FiFilter className="filter-icon" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="date-filter"
            />
          </div>

          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={validatedOnly}
              onChange={(e) => setValidatedOnly(e.target.checked)}
            />
            <span className="slider"></span>
            <span>Validated Only</span>
          </label>

          <button
            onClick={fetchCheckins}
            className="refresh-btn"
            title="Refresh check-ins"
          >
            <FiRefreshCw /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner">Loading check-ins...</div>
      ) : (
        <div className="table-container">
          <div className="stats-bar">
            <span>Total: {checkins.length}</span>
            <span>Validated: {checkins.filter(c => c.validated).length}</span>
            <span>Pending: {checkins.filter(c => !c.validated).length}</span>
          </div>

          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Car Wash</th>
                <th>Check-in Time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {checkins.length > 0 ? (
                checkins.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div className="user-info">
                        <FiUser className="user-icon" />
                        <div>
                          <div className="user-email">{c.users?.email || "—"}</div>
                          <div className="username">@{c.username || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td>{c.carwashes?.name || `ID: ${c.carwash_id}`}</td>
                    <td>
                      <div className="time-info">
                        <div>{formatDateTime(c.check_in_time)}</div>
                        <div className="time-ago">{getTimeAgo(c.check_in_time)}</div>
                      </div>
                    </td>
                    <td>
                      {c.validated ? (
                        <span className="status validated">
                          <FiCheckCircle /> Validated
                        </span>
                      ) : (
                        <span className="status pending">
                          <FiXCircle /> Pending
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        {!c.validated ? (
                          <button
                            onClick={() => validateCheckin(c.id)}
                            className="validate-btn"
                            title="Mark as validated"
                          >
                            <FiCheckCircle /> Validate
                          </button>
                        ) : (
                          <button
                            onClick={() => resetCheckin(c.id)}
                            className="reset-btn"
                            title="Reset validation (allows user to check in again)"
                          >
                            <FiRefreshCw /> Reset
                          </button>
                        )}
                        <button
                          onClick={() => deleteCheckin(c.id)}
                          className="delete-btn"
                          title="Delete check-in record"
                        >
                          <FiXCircle /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="no-results">
                    No check-ins found matching filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Checkins;